import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from 'admin/sidebar/AdminSidebar';
import axios from 'axios';
import './Locations.css';

const fmtAED = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend.washta.com';
const LOCATIONS_DETAIL_URL = `${API_BASE}/api/admin/v2/dashboard/locations-detail`;
const TOGGLE_FLAG_URL = (locationId) => `${API_BASE}/api/package-sets/admin/locations/${locationId}/toggle-flag`;

const RATING_THRESHOLD = 4.0;
const ACCEPT_TIME_THRESHOLD = 10;

const LocationsSkeleton = () => (
   <div className="ws-view-block" style={{ width: '100%' }}>
      <div className="ws-sec-head" style={{ alignItems: 'flex-start', marginBottom: 16 }}>
         <div>
            <div className="ws-skel ws-skel-title"></div>
            <div className="ws-skel ws-skel-sub"></div>
         </div>
      </div>
      <div style={{ display: 'flex', gap: 7, marginBottom: 14 }}>
         {Array.from(new Array(4)).map((_, i) => (
            <div key={i} className="ws-skel ws-skel-pill"></div>
         ))}
      </div>
      <div className="ws-skel ws-skel-block" style={{ height: 320 }}></div>
   </div>
);

const AdminLocations = () => {
   const navigate = useNavigate();
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [locations, setLocations] = useState([]);
   const [providerFilter, setProviderFilter] = useState('All Providers');
   const [cityFilter, setCityFilter] = useState('All Cities');
   const [serviceFilter, setServiceFilter] = useState('All Services');
   const [search, setSearch] = useState('');
   const [togglingId, setTogglingId] = useState(null);

   useEffect(() => {
      window.scrollTo(0, 0);
      const fetchLocations = async () => {
         try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('accessTokenAdmin');
            if (!token) { setError('Not authenticated'); return; }
            const res = await axios.get(LOCATIONS_DETAIL_URL, {
               headers: { Authorization: `Bearer ${token}` },
            });
            setLocations(res.data?.data?.locations || []);
         } catch (err) {
            console.error('Error fetching locations:', err);
            setError('Failed to load locations');
         } finally {
            setLoading(false);
         }
      };
      fetchLocations();
   }, []);

   const providers = useMemo(
      () => ['All Providers', ...Array.from(new Set(locations.map((l) => l.provider).filter(Boolean)))],
      [locations]
   );
   const cities = useMemo(
      () => ['All Cities', ...Array.from(new Set(locations.map((l) => l.area).filter(Boolean)))],
      [locations]
   );
   const services = useMemo(
      () => ['All Services', ...Array.from(new Set(locations.map((l) => l.service).filter(Boolean)))],
      [locations]
   );

   const filteredLocations = useMemo(() => {
      return locations
         .filter((l) => providerFilter === 'All Providers' || l.provider === providerFilter)
         .filter((l) => cityFilter === 'All Cities' || l.area === cityFilter)
         .filter((l) => serviceFilter === 'All Services' || l.service === serviceFilter)
         .filter((l) => {
            if (!search.trim()) return true;
            const q = search.trim().toLowerCase();
            return (
               l.name?.toLowerCase().includes(q) ||
               l.area?.toLowerCase().includes(q) ||
               l.provider?.toLowerCase().includes(q)
            );
         })
         .sort((a, b) => b.gmv30d - a.gmv30d);
   }, [locations, providerFilter, cityFilter, serviceFilter, search]);

   const flaggedLocations = useMemo(
      () => filteredLocations.filter(
         (l) => (l.avgRating != null && l.avgRating < RATING_THRESHOLD) ||
                (l.avgAcceptMin != null && l.avgAcceptMin > ACCEPT_TIME_THRESHOLD)
      ),
      [filteredLocations]
   );

   const maxTrendVal = useMemo(
      () => Math.max(1, ...locations.flatMap((l) => l.trend || [])),
      [locations]
   );

   const handleToggleSingleWash = async (loc) => {
      setTogglingId(loc.id);
      try {
         const token = localStorage.getItem('accessTokenAdmin');
         await axios.patch(
            TOGGLE_FLAG_URL(loc.id),
            { shopId: loc.shopId, flag: 'singleWashEnabled' },
            { headers: { Authorization: `Bearer ${token}` } }
         );
         setLocations((prev) => prev.map((l) => (
            l.id === loc.id ? { ...l, singleWashEnabled: !l.singleWashEnabled } : l
         )));
      } catch (err) {
         console.error('Error toggling single wash flag:', err);
      } finally {
         setTogglingId(null);
      }
   };

   const exportCSV = () => {
      const header = ['#', 'Location', 'Area', 'Provider', 'Service', 'Orders', 'GMV', 'Commission', 'Avg Rating', 'Avg Accept Time (min)', 'Status'];
      const lines = filteredLocations.map((l, i) => [
         i + 1, l.name, l.area, l.provider, l.service,
         l.orders30d, l.gmv30d, l.commission,
         l.avgRating != null ? l.avgRating : 'N/A',
         l.avgAcceptMin != null ? l.avgAcceptMin : 'N/A',
         l.status === 'active' ? 'Active' : 'Needs review',
      ]);
      const csv = [header, ...lines]
         .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
         .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'washta-locations.csv';
      a.click();
      URL.revokeObjectURL(url);
   };

   return (
      <div className='mainDiv-all-sideBar'>
         <div className='div-sidebar'>
            <AdminSidebar />
         </div>
         <div className='div-Dashbaord' style={{ padding: 0, background: 'white', width: '100%' }}>
            <div className="dashboard ws-locations-dashboard" style={{ width: '100%', padding: 0 }}>
               {loading ? (
                  <LocationsSkeleton />
               ) : error ? (
                  <div className="ws-view-block">
                     <div style={{ color: '#dc2626', fontSize: 13, padding: 20 }}>{error}</div>
                  </div>
               ) : (
                  <div className="ws-view-block" style={{ width: '100%', boxSizing: 'border-box' }}>
                     <div className="ws-sec-head" style={{ alignItems: 'flex-start', marginBottom: 4 }}>
                        <div>
                           <div className="ws-view-title">Locations</div>
                           <div className="ws-view-sub">
                              All active locations across all providers, ranked by GMV. Rating highlighted amber when
                              below {RATING_THRESHOLD.toFixed(1)} and accept time highlighted red when above {ACCEPT_TIME_THRESHOLD} min.
                           </div>
                        </div>
                        <button className="ws-btn-ex" onClick={exportCSV}>↓ Export CSV</button>
                     </div>

                     <div className="ws-filter-row">
                        <select className="ws-f-select" value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
                           {providers.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <select className="ws-f-select" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
                           {services.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <input
                           className="ws-f-input ws-ml"
                           placeholder="Search location…"
                           style={{ width: 200 }}
                           value={search}
                           onChange={(e) => setSearch(e.target.value)}
                        />
                     </div>

                     <table className="ws-tbl">
                        <thead>
                           <tr>
                              <th>#</th>
                              <th>Location</th>
                              <th>Provider · Service</th>
                              <th className="ws-r">Orders</th>
                              <th className="ws-r">GMV</th>
                              <th className="ws-r">Commission</th>
                              <th className="ws-r">Avg Rating</th>
                              <th className="ws-r">Avg Accept Time</th>
                              <th>Trend (5w)</th>
                              <th>Status</th>
                              {/* <th>Single wash</th> */}
                           </tr>
                        </thead>
                        <tbody>
                           {filteredLocations.length === 0 ? (
                              <tr>
                                 <td colSpan={11} style={{ textAlign: 'center', color: '#aaa', padding: '18px 10px' }}>
                                    No locations found.
                                 </td>
                              </tr>
                           ) : filteredLocations.map((loc, i) => {
                              const isTop3 = i < 3;
                              const ratingLow = loc.avgRating != null && loc.avgRating < RATING_THRESHOLD;
                              const acceptHigh = loc.avgAcceptMin != null && loc.avgAcceptMin > ACCEPT_TIME_THRESHOLD;
                              const needsReview = loc.status === 'review';
                              return (
                                 <tr
                                    key={loc.id}
                                    style={needsReview ? { background: '#fffbeb', cursor: 'default' } : { cursor: 'default' }}
                                 >
                                    <td style={{ fontWeight: 700, color: isTop3 ? '#4a3db8' : '#888' }}>{i + 1}</td>
                                    <td>
                                       <div style={{ fontWeight: 700, fontSize: 11 }}>{loc.name}</div>
                                       <div style={{ fontSize: 9, color: '#aaa' }}>{loc.area || '—'}</div>
                                    </td>
                                    <td>
                                       <div style={{ fontSize: 11 }}>{loc.provider}</div>
                                       <div style={{ fontSize: 9, color: '#aaa' }}>{loc.service}</div>
                                    </td>
                                    <td className="ws-r">{fmtNum(loc.orders30d)}</td>
                                    <td className="ws-r">{fmtAED(loc.gmv30d)}</td>
                                    <td className="ws-r" style={{ color: '#4a3db8' }}>{fmtAED(loc.commission)}</td>
                                    <td className="ws-r" style={ratingLow ? { color: '#d97706', fontWeight: 700 } : undefined}>
                                       {loc.avgRating != null ? `⭐ ${loc.avgRating.toFixed(1)}` : '—'}
                                    </td>
                                    <td
                                       className="ws-r"
                                       style={{
                                          fontWeight: 700,
                                          color: loc.avgAcceptMin == null ? '#aaa'
                                             : acceptHigh ? '#dc2626'
                                             : loc.avgAcceptMin <= 5 ? '#1a6641'
                                             : '#d97706',
                                       }}
                                    >
                                       {loc.avgAcceptMin != null ? `${loc.avgAcceptMin} min${acceptHigh ? ' ⚠' : ''}` : '—'}
                                    </td>
                                    <td>
                                       <div className="ws-sparkline">
                                          {(loc.trend || [0, 0, 0, 0, 0]).map((v, idx) => (
                                             <div
                                                key={idx}
                                                className={`ws-spark-bar${v >= maxTrendVal * 0.6 ? ' hi' : ''}`}
                                                style={{ height: `${Math.max(4, (v / maxTrendVal) * 22)}px` }}
                                             ></div>
                                          ))}
                                       </div>
                                    </td>
                                    <td>
                                       <span className={`ws-pill ${needsReview ? 'ws-p-review' : 'ws-p-active'}`}>
                                          {needsReview ? 'Needs review' : 'Active'}
                                       </span>
                                    </td>
                                    {/* <td>
                                       <button
                                          className={`ws-toggle ${loc.singleWashEnabled ? 'on' : ''}`}
                                          disabled={togglingId === loc.id}
                                          onClick={() => handleToggleSingleWash(loc)}
                                       >
                                          <span className="ws-toggle-knob" />
                                       </button>
                                    </td> */}
                                 </tr>
                              );
                           })}
                        </tbody>
                     </table>

                     {flaggedLocations.length > 0 && (
                        <div className="ws-warn-banner" style={{ marginTop: 12 }}>
                           ⚠️ {flaggedLocations.map((l) => {
                              const issues = [];
                              if (l.avgRating != null && l.avgRating < RATING_THRESHOLD) issues.push(`⭐ ${l.avgRating.toFixed(1)}`);
                              if (l.avgAcceptMin != null && l.avgAcceptMin > ACCEPT_TIME_THRESHOLD) issues.push(`${l.avgAcceptMin} min accept`);
                              return `${l.name} (${issues.join(' · ')})`;
                           }).join(' and ')}{' '}
                           {flaggedLocations.length > 1 ? 'are' : 'is'} below quality thresholds. Investigate with the respective providers.
                        </div>
                     )}

                     {locations.length === 0 && !loading && (
                        <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0', fontSize: 13 }}>
                           No location data available yet. Orders with shop assignments will appear here.
                        </div>
                     )}
                  </div>
               )}
            </div>
         </div>
      </div>
   );
};

export default AdminLocations;
